import { useQuery } from '@tanstack/react-query';
import { requestTyped } from '@xdocs/ws-protocol';
import type { CreditsBalanceResult, ErrorAlert } from '@xdocs/schemas';
import { useAuth } from '../../auth/AuthProvider.js';
import { useWs } from '../../ws/WsProvider.js';

/**
 * PLACEHOLDER product decision (no numeric threshold is specified anywhere
 * in FEATURES.md/CODE_GUIDELINES.md/CLAUDE_DESIGN.md for F47's "low (amber)"
 * state) — flagged as an open issue. Easily tunable once billing defines it.
 */
const LOW_BALANCE_THRESHOLD = 20;

export interface UseCreditsBalanceResult {
  /** `null` = not fetched yet (skeleton-first-paint per `<CreditsIndicator>`'s own contract). */
  balance: number | null;
  isLoading: boolean;
  isError: boolean;
  low: boolean;
  zero: boolean;
}

/**
 * Live credit balance (F19/F47). `users.credits_balance` is NEVER cached
 * (E1) and is the live authority — this hook only reads it via `credits.
 * balance`, keyed under `['user', userId, 'credits', 'balance']` so it is
 * refetched automatically by the generic `user:{userId}` push-invalidation
 * skeleton on every ledger write (E1 §21.3: "every write... pushes
 * `user:{userId}`") — never polled.
 */
export function useCreditsBalance(): UseCreditsBalanceResult {
  const { userId } = useAuth();
  const { wsClient, connectionState } = useWs();
  const enabled = connectionState === 'connected' && userId !== null;

  const query = useQuery<CreditsBalanceResult, ErrorAlert>({
    queryKey: ['user', userId, 'credits', 'balance'],
    queryFn: () => requestTyped(wsClient, 'credits.balance', undefined),
    enabled,
  });

  const balance = query.data?.balance ?? null;

  return {
    balance,
    isLoading: query.isLoading,
    isError: query.isError,
    low: balance !== null && balance > 0 && balance < LOW_BALANCE_THRESHOLD,
    zero: balance === 0,
  };
}
